import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import {useParams} from 'react-router';
import {Concept} from 'types';
import {useLocalization} from 'localization';
import type {Data} from 'schemaDefinition/types/values';
import {
  useGetSeriesQuery,
  useMergeSeriesMutation,
} from 'services/series/seriesApi';
import {assertCollectionValue} from 'services/utils';
import {mapRtkErrorToHttpError} from 'store/api';
import {Scene, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent';
import {useSchema} from 'schemas';
import {CompareEntities} from 'widgets/compareEntities';
import {useCollectionFormConfiguration} from 'editors/collections/hooks/useCollectionFormConfiguration';
import {useSceneExtensionsContext} from 'sceneExtensions';

export const MergeSeriesSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();
  const {currentExtension} = useSceneExtensionsContext();
  const configuration = useCollectionFormConfiguration();

  const [mergeSeries] = useMergeSeriesMutation();
  const {targetId: targetIdParam, sourceId: sourceIdParam} = useParams();
  const targetId = targetIdParam || '';
  const sourceId = sourceIdParam || '';

  const {
    data: targetData,
    status: targetStatus,
    error: targetError,
  } = useGetSeriesQuery({
    id: targetId,
  });
  const {
    data: sourceData,
    status: sourceStatus,
    error: sourceError,
  } = useGetSeriesQuery({
    id: sourceId,
  });

  const schema = useSchema({
    entity: Concept.collection,
    subType: targetData?.collectionType ?? Concept.series,
  });

  const onSave = useCallback(
    (target: Data, source: Data) => {
      return mergeSeries({
        target: assertCollectionValue(target),
        source: assertCollectionValue(source),
      })
        .unwrap()
        .then(() => {
          successSnack('Seriene ble slått sammen');
          return Promise.resolve();
        })
        .catch(error => {
          errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
          return Promise.reject();
        });
    },
    [errorSnack, mergeSeries, successSnack],
  );

  return (
    <Scene
      browserTitle={t('appbar.mergeSeries')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={t('appbar.mergeSeries')} />
      <Scene.Content>
        <RtkContent status={targetStatus} error={targetError}>
          <RtkContent status={sourceStatus} error={sourceError}>
            {targetData && sourceData ? (
              <CompareEntities
                compareEntitiesContextProps={{
                  targetData,
                  sourceData,
                  schema,
                  onSave,
                }}
                compareEntitiesFormProps={{
                  allowEdit: true,
                  formConfiguration: configuration,
                  targetHeader: (
                    <Box
                      sx={{
                        ml: 2,
                      }}>
                      <Text
                        kind="primary"
                        variant="h4"
                        textTransform="uppercase">
                        Serie som beholdes
                      </Text>
                      <Text kind="primary" variant="caption">
                        Denne serien vil få alle koblingene, og eventuelle
                        endringer som gjøres.
                      </Text>
                    </Box>
                  ),
                  sourceHeader: (
                    <Box
                      sx={{
                        ml: 2,
                      }}>
                      <Text
                        kind="primary"
                        variant="h4"
                        textTransform="uppercase">
                        Serie som slettes
                      </Text>
                      <Text kind="primary" variant="caption">
                        Koblingene vil bli flyttet fra denne serien til den
                        andre serien, og denne serien vil bli slettet etterpå.
                      </Text>
                    </Box>
                  ),
                }}
              />
            ) : null}
          </RtkContent>
        </RtkContent>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
